// src/components/Athletes/AthleteEventProfile.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, Chip, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Alert, Skeleton, useTheme, useMediaQuery,
  Divider, Stack, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useEventContext } from '../../context/EventContext';
import { formatWithPrecision } from '../../utils/precision';
import { isSpecial, getFontSize } from '../Eventos/EventsHeats/utils/eventHelpers';
import { 
  getCurrentUserProfile, 
  linkAthleteToOwnAccount, 
  requestAthleteForTeam,
  getActionTypeForUser 
} from '../../services/EnhancedAthleteService';

const AthleteEventProfile = () => {
  const params = useParams();
  const { competitionId, athleteId, id } = params;
  // El athleteId puede venir como 'athleteId' o como 'id' dependiendo de la ruta
  const actualAthleteId = athleteId || id;
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  // DEBUG: Agregar logs para ver qué está pasando
  console.log('🔍 AthleteEventProfile - Params:', params);
  console.log('🔍 AthleteEventProfile - competitionId:', competitionId);
  console.log('🔍 AthleteEventProfile - actualAthleteId:', actualAthleteId);

  const {
    evtData,
    getParticipantResults,
    participantsResults,
    getPlace,
    precision: globalPrecision,
    isTrackEventFn
  } = useEventContext();

  // DEBUG: Verificar contexto
  console.log('🔍 AthleteEventProfile - evtData length:', evtData?.length || 0);
  console.log('🔍 AthleteEventProfile - evtData sample:', evtData?.[0]);

  const [athleteData, setAthleteData] = useState(null);
  const [athleteEvents, setAthleteEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Buscar información del atleta en todos los eventos
  const athleteInfo = useMemo(() => {
    console.log('🔍 athleteInfo - Buscando atleta:', actualAthleteId);
    console.log('🔍 athleteInfo - evtData disponible:', evtData?.length || 0);
    
    if (!evtData.length || !actualAthleteId) {
      console.log('❌ athleteInfo - No hay datos o athleteId');
      return null;
    }
    
    for (const event of evtData) {
      console.log('🔍 Revisando evento:', event.eventNumber, 'participantes:', event.participants?.length || 0);
      const participant = event.participants?.find(p => p.idNumber === actualAthleteId);
      if (participant) {
        console.log('✅ Atleta encontrado:', participant);
        return {
          ...participant,
          fullName: participant.fullName || `${participant.firstName || ''} ${participant.lastName || ''}`.trim()
        };
      }
    }
    console.log('❌ Atleta no encontrado en ningún evento');
    return null;
  }, [evtData, actualAthleteId]);

  // Obtener todos los eventos del atleta
  useEffect(() => {
    console.log('🔍 useEffect - Iniciando búsqueda de eventos');
    console.log('🔍 useEffect - evtData length:', evtData?.length || 0);
    console.log('🔍 useEffect - actualAthleteId:', actualAthleteId);
    
    if (!evtData.length || !actualAthleteId) {
      console.log('❌ useEffect - Faltan datos, saliendo');
      setLoading(false);
      return;
    }

    const events = [];
    
    evtData.forEach((event, eventIndex) => {
      console.log(`🔍 Procesando evento ${eventIndex}:`, event.eventNumber, event.eventName);
      const participant = event.participants?.find(p => p.idNumber === actualAthleteId);
      if (participant) {
        console.log('✅ Atleta encontrado en evento:', event.eventNumber);
        // Obtener resultados del atleta para este evento
        const results = getParticipantResults(
          event.eventNumber, 
          event.round, 
          event.heat, 
          actualAthleteId
        ) || {};
        
        console.log('🔍 Resultados para evento:', event.eventNumber, results);
        
        const place = getPlace(event.eventNumber, event.round, event.heat, actualAthleteId);
        const isTrack = isTrackEventFn(event.eventCode);
        
        // Determinar el estado del evento
        let status = 'pending'; // pending, completed, in_progress
        let resultDisplay = '-';
        
        if (results.time || results.special || place) {
          status = 'completed';
          
          if (results.special && isSpecial(results.special)) {
            resultDisplay = results.special;
          } else if (results.time) {
            resultDisplay = results.time;
          } else if (results.timeRaw) {
            resultDisplay = formatWithPrecision(results.timeRaw, globalPrecision);
          } else if (!isTrack && results.attempts?.length > 0) {
            // Para eventos de campo, mostrar la mejor marca
            const validAttempts = results.attempts.filter(a => 
              a && a.result && a.result !== '-' && !isSpecial(a.result)
            );
            if (validAttempts.length > 0) {
              const best = validAttempts.reduce((prev, curr) => 
                parseFloat(curr.result) > parseFloat(prev.result) ? curr : prev
              );
              resultDisplay = best.result;
            }
          } else if (!isTrack && results.heights && typeof results.heights === 'object') {
            // Para eventos verticales (salto alto, pértiga)
            const heights = Object.entries(results.heights);
            let bestHeight = 0;
            
            heights.forEach(([heightStr, heightData]) => {
              if (heightData.attempts && heightData.attempts.includes('O')) {
                const height = parseFloat(heightStr);
                if (height > bestHeight) {
                  bestHeight = height;
                }
              }
            });
            
            if (bestHeight > 0) {
              resultDisplay = `${bestHeight.toFixed(2)}m`;
            }
          }
        }

        const eventData = {
          eventNumber: event.eventNumber,
          eventName: event.eventName,
          eventCode: event.eventCode,
          round: event.round,
          heat: event.heat,
          lane: participant.lane || '-',
          status,
          place,
          result: resultDisplay,
          isTrack,
          wind: results.wind,
          reactionTime: results.reactionTime,
          chips: {
            PB: results.PB,
            SB: results.SB,
            MR: results.MR,
            qualifier: results.qualifier
          },
          // Ordenamiento
          eventOrder: event.eventNumber,
          roundOrder: event.round,
          heatOrder: event.heat
        };
        
        console.log('✅ Evento procesado:', eventData);
        events.push(eventData);
      }
    });

    console.log('🔍 Total eventos encontrados:', events.length);

    // Ordenar eventos por número de evento, ronda y heat
    events.sort((a, b) => {
      if (a.eventNumber !== b.eventNumber) return a.eventNumber - b.eventNumber;
      if (a.round !== b.round) return a.round - b.round;
      return a.heat - b.heat;
    });

    setAthleteEvents(events);
    setAthleteData(athleteInfo);
    setLoading(false);
    
  }, [evtData, actualAthleteId, getParticipantResults, getPlace, athleteInfo, globalPrecision, isTrackEventFn]);

  // Verificar tipo de usuario y acción disponible
  useEffect(() => {
    const checkUserAction = async () => {
      if (actualAthleteId && competitionId) {
        try {
          const profile = await getCurrentUserProfile();
          const action = await getActionTypeForUser(actualAthleteId, competitionId);
          
          setUserProfile(profile);
          setActionType(action);
        } catch (error) {
          console.error('Error verificando usuario:', error);
          setActionType({ type: 'error' });
        }
      }
    };

    checkUserAction();
  }, [actualAthleteId, competitionId]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEventClick = (eventNumber, round) => {
    navigate(`/${competitionId}/event/${eventNumber}/${round}`);
  };

  const handleTeamClick = (teamName) => {
    console.log('🔍 AthleteEventProfile - handleTeamClick llamado con:', teamName);
    console.log('🔍 AthleteEventProfile - competitionId:', competitionId);
    const encodedTeamName = encodeURIComponent(teamName);
    const fullUrl = `/${competitionId}/team/${encodedTeamName}`;
    console.log('🔍 AthleteEventProfile - URL completa:', fullUrl);
    
    navigate(fullUrl);
  };

  const handleLinkAthleteClick = () => {
    if (actionType?.type === 'login_required') {
      // Redirigir al login
      navigate('/auth/login', { 
        state: { 
          returnUrl: `/${competitionId}/athlete/${actualAthleteId}`,
          message: 'Inicia sesión para vincular este atleta a tu cuenta'
        }
      });
      return;
    }
    
    setLinkDialogOpen(true);
  };

  const handleLinkConfirm = async () => {
    try {
      setActionLoading(true);
      
      if (actionType?.type === 'athlete_claim') {
        // Atleta vinculándose a sí mismo
        await linkAthleteToOwnAccount(actualAthleteId, competitionId, {
          fullName: athleteData.fullName,
          affiliation: athleteData.affiliation,
          idNumber: athleteData.idNumber
        });
        
        setLinkSuccess(true);
        setActionType({ type: 'already_linked' });
        
      } else if (actionType?.type === 'coach_request') {
        // Entrenador solicitando agregar atleta
        const result = await requestAthleteForTeam(actualAthleteId, competitionId, {
          fullName: athleteData.fullName,
          affiliation: athleteData.affiliation,
          idNumber: athleteData.idNumber
        });
        
        setLinkSuccess(true);
        // Mostrar mensaje diferente para entrenadores
        setTimeout(() => {
          setLinkSuccess(false);
        }, 5000);
      }
      
      setLinkDialogOpen(false);
      
      // Ocultar mensaje de éxito después de un tiempo
      setTimeout(() => {
        setLinkSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error en la acción:', error);
      
      let errorMessage = 'Error desconocido';
      switch (error.message) {
        case 'AUTHENTICATION_REQUIRED':
          errorMessage = 'Debes iniciar sesión primero';
          break;
        case 'ATHLETE_ALREADY_LINKED':
          errorMessage = 'Ya tienes un atleta vinculado a tu cuenta';
          break;
        case 'COACH_CANNOT_LINK_AS_ATHLETE':
          errorMessage = 'Los entrenadores no pueden vincularse como atletas';
          break;
        case 'REQUEST_ALREADY_EXISTS':
          errorMessage = 'Ya enviaste una solicitud a este atleta';
          break;
        default:
          errorMessage = 'Error al procesar la solicitud';
      }
      
      alert(errorMessage); // TODO: Reemplazar con Snackbar
    } finally {
      setActionLoading(false);
    }
  };

  const getButtonConfig = () => {
    if (!actionType) return null;
    
    switch (actionType.type) {
      case 'login_required':
        return {
          text: 'Iniciar Sesión',
          icon: <PersonAddIcon />,
          color: 'primary',
          variant: 'outlined'
        };
      case 'athlete_claim':
        return {
          text: isXs ? 'Mi Perfil' : 'Este es mi perfil',
          icon: <PersonAddIcon />,
          color: 'success',
          variant: 'contained'
        };
      case 'coach_request':
        return {
          text: isXs ? 'Agregar' : 'Agregar a mi equipo',
          icon: <PersonAddIcon />,
          color: 'primary',
          variant: 'contained'
        };
      case 'already_linked':
        return null; // No mostrar botón
      default:
        return null;
    }
  };

  const getDialogConfig = () => {
    if (!actionType) return null;
    
    if (actionType.type === 'athlete_claim') {
      return {
        title: 'Confirmar Perfil de Atleta',
        description: `¿Confirmas que eres ${athleteData?.fullName}?`,
        alert: {
          type: 'info',
          message: 'Al confirmar, este perfil de atleta se vinculará a tu cuenta y podrás ver tus resultados y estadísticas.'
        },
        confirmText: 'Sí, este soy yo',
        confirmIcon: <CheckCircleIcon />
      };
    } else if (actionType.type === 'coach_request') {
      return {
        title: 'Agregar Atleta al Equipo',
        description: `¿Quieres enviar una invitación a ${athleteData?.fullName} para que se una a tu equipo?`,
        alert: {
          type: 'warning',
          message: 'Se enviará una notificación al atleta. Él/ella debe aceptar la invitación para unirse a tu equipo.'
        },
        confirmText: 'Enviar invitación',
        confirmIcon: <PersonAddIcon />
      };
    }
    
    return null;
  };

  const handleLinkCancel = () => {
    setLinkDialogOpen(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'in_progress':
        return <ScheduleIcon color="warning" fontSize="small" />;
      default:
        return <PendingIcon color="action" fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'in_progress':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Estadísticas del atleta
  const stats = useMemo(() => {
    const completed = athleteEvents.filter(e => e.status === 'completed').length;
    const pending = athleteEvents.filter(e => e.status === 'pending').length;
    
    return { completed, pending, total: athleteEvents.length };
  }, [athleteEvents]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="100%" height={60} />
          <Skeleton variant="text" width="80%" height={40} />
        </Paper>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
        ))}
      </Box>
    );
  }

  if (error || !athleteData) {
    return (
      <Box sx={{ p: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        <Alert severity="error" variant="outlined">
          <Typography variant="h6" gutterBottom>
            Atleta no encontrado
          </Typography>
          <Typography variant="body2">
            No se pudo encontrar información para el atleta con ID: {actualAthleteId}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isXs ? 1 : 2,
      maxWidth: '100%',
      backgroundColor: theme.palette.background.default
    }}>
      {/* Header con botón de regreso */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleBackClick}
            sx={{ 
              mr: 2,
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.primary.main,
              fontSize: getFontSize(24, 1, isXs, isSm, isMd)
            }}
          >
            Perfil del Atleta en Competencia
          </Typography>
        </Box>

        {/* Botón dinámico según el tipo de usuario */}
        {(() => {
          const buttonConfig = getButtonConfig();
          
          if (!buttonConfig) {
            // Si no hay botón, mostrar estado actual
            if (actionType?.type === 'already_linked') {
              return (
                <Chip
                  label={userProfile?.userType === 'coach' ? 'En tu equipo' : 'Tu perfil'}
                  color="primary"
                  variant="filled"
                  icon={<CheckCircleIcon />}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd)
                  }}
                />
              );
            }
            return null;
          }

          return (
            <Button
              variant={buttonConfig.variant}
              color={buttonConfig.color}
              startIcon={buttonConfig.icon}
              onClick={handleLinkAthleteClick}
              size={isXs ? "small" : "medium"}
              disabled={actionLoading}
              sx={{
                fontWeight: 'bold',
                borderRadius: 2,
                px: isXs ? 2 : 3
              }}
            >
              {actionLoading ? 'Procesando...' : buttonConfig.text}
            </Button>
          );
        })()}

        {/* Mensaje de éxito */}
        {linkSuccess && (
          <Chip
            label={
              actionType?.type === 'coach_request' 
                ? '¡Invitación enviada!' 
                : '¡Perfil vinculado!'
            }
            color="success"
            variant="filled"
            icon={<CheckCircleIcon />}
            sx={{
              fontWeight: 'bold',
              fontSize: getFontSize(14, 1, isXs, isSm, isMd),
              ml: 2
            }}
          />
        )}
      </Box>

      {/* Información del atleta */}
      <Paper
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.background.paper})`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {/* Avatar del atleta */}
          <Avatar
            sx={{
              width: isXs ? 60 : 80,
              height: isXs ? 60 : 80,
              bgcolor: theme.palette.primary.main,
              fontSize: isXs ? 24 : 32,
              fontWeight: 'bold',
              mr: 2,
              boxShadow: theme.shadows[3]
            }}
          >
            {athleteData.fullName ? athleteData.fullName.charAt(0).toUpperCase() : <PersonIcon />}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            {/* Nombre y número del atleta */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  fontSize: getFontSize(28, 1, isXs, isSm, isMd)
                }}
              >
                {athleteData.fullName}
              </Typography>
              <Chip 
                label={`#${athleteData.idNumber}`}
                variant="filled"
                color="primary"
                size={isXs ? "medium" : "large"}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: getFontSize(16, 1, isXs, isSm, isMd)
                }}
              />
            </Box>
            
            {/* Equipo con avatar */}
            {athleteData.affiliation && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  mb: 2,
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    transform: 'translateX(4px)'
                  }
                }}
                onClick={() => {
                  console.log('🔍 AthleteEventProfile - Equipo clickeado:', athleteData.affiliation);
                  handleTeamClick(athleteData.affiliation);
                }}
              >
                <Avatar
                  sx={{
                    width: isXs ? 32 : 40,
                    height: isXs ? 32 : 40,
                    bgcolor: theme.palette.secondary.main,
                    fontSize: isXs ? 14 : 16,
                    fontWeight: 'bold',
                    boxShadow: theme.shadows[2]
                  }}
                >
                  {athleteData.affiliation.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      fontSize: getFontSize(18, 1, isXs, isSm, isMd),
                      textDecoration: 'none',
                      borderBottom: `2px dotted ${theme.palette.secondary.main}`,
                      '&:hover': {
                        color: theme.palette.secondary.main,
                        borderBottomStyle: 'solid'
                      }
                    }}
                  >
                    {athleteData.affiliation}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: getFontSize(12, 1, isXs, isSm, isMd) }}
                  >
                    Click para ver equipo
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Chips de estadísticas */}
            {athleteData.year && (
              <Chip 
                label={`Año: ${athleteData.year}`}
                variant="outlined"
                size={isXs ? "small" : "medium"}
                sx={{ mt: 1, mr: 1 }}
              />
            )}
            <Chip 
              label={`${stats.total} eventos`}
              variant="outlined"
              color="primary"
              size={isXs ? "small" : "medium"}
              sx={{ mt: 1, mr: 1 }}
            />
            <Chip 
              label={`${stats.completed} completados`}
              variant="filled"
              color="success"
              size={isXs ? "small" : "medium"}
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        {/* Sin estadísticas adicionales - más limpio */}
        <Divider sx={{ my: 2 }} />
      </Paper>

      {/* Lista de eventos */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.primary.main,
              fontSize: getFontSize(18, 1, isXs, isSm, isMd)
            }}
          >
            Eventos del Atleta ({athleteEvents.length})
          </Typography>
        </Box>

        {athleteEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <EventIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No hay eventos registrados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Este atleta no está inscrito en ningún evento.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size={isXs ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Evento
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Ronda/Heat
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Línea
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Lugar
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                    Resultado
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {athleteEvents.map((event, index) => (
                  <TableRow 
                    key={`${event.eventNumber}-${event.round}-${event.heat}`}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                    onClick={() => handleEventClick(event.eventNumber, event.round)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon(event.status)}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            ml: 1, 
                            color: getStatusColor(event.status),
                            fontWeight: 'medium',
                            fontSize: getFontSize(13, 1, isXs, isSm, isMd)
                          }}
                        >
                          {event.status === 'completed' ? 'Completado' : 
                           event.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'medium',
                            fontSize: getFontSize(14, 1, isXs, isSm, isMd)
                          }}
                        >
                          {event.eventName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: getFontSize(12, 1, isXs, isSm, isMd) }}
                        >
                          Evento #{event.eventNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        label={`R${event.round}/H${event.heat}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: getFontSize(12, 1, isXs, isSm, isMd) }}
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography sx={{ fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                        {event.lane}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      {event.place && event.place !== '-' ? (
                        <Chip 
                          label={event.place}
                          size="small"
                          variant="filled"
                          color={parseInt(event.place) <= 3 ? 'warning' : 'default'}
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: getFontSize(12, 1, isXs, isSm, isMd)
                          }}
                        />
                      ) : (
                        <Typography 
                          color="text.secondary"
                          sx={{ fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography 
                        sx={{ 
                          fontWeight: event.result !== '-' ? 'bold' : 'normal',
                          color: isSpecial(event.result) 
                            ? theme.palette.error.main 
                            : theme.palette.text.primary,
                          fontSize: getFontSize(14, 1, isXs, isSm, isMd)
                        }}
                      >
                        {event.result}
                      </Typography>
                      {event.wind && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            fontSize: getFontSize(11, 1, isXs, isSm, isMd)
                          }}
                        >
                          {event.wind}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de confirmación dinámico */}
      <Dialog
        open={linkDialogOpen}
        onClose={handleLinkCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        {(() => {
          const dialogConfig = getDialogConfig();
          if (!dialogConfig) return null;

          return (
            <>
              <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                pb: 1
              }}>
                <Avatar
                  sx={{
                    bgcolor: actionType?.type === 'coach_request' 
                      ? theme.palette.primary.main 
                      : theme.palette.success.main,
                    width: 40,
                    height: 40
                  }}
                >
                  {actionType?.type === 'coach_request' ? <PersonAddIcon /> : <CheckCircleIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {dialogConfig.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {actionType?.type === 'coach_request' 
                      ? `Como entrenador de ${userProfile?.teamName || 'tu equipo'}`
                      : 'Vincula tu perfil de atleta'
                    }
                  </Typography>
                </Box>
              </DialogTitle>
              
              <DialogContent sx={{ py: 2 }}>
                <DialogContentText sx={{ mb: 2, fontSize: '16px' }}>
                  {dialogConfig.description}
                </DialogContentText>
                
                <Alert severity={dialogConfig.alert.type} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {dialogConfig.alert.message}
                  </Typography>
                </Alert>

                <Box sx={{ 
                  p: 2, 
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {actionType?.type === 'coach_request' ? 'Atleta a invitar:' : 'Tu perfil de atleta:'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: theme.palette.primary.main,
                        fontSize: 20,
                        fontWeight: 'bold'
                      }}
                    >
                      {athleteData?.fullName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {athleteData?.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        #{athleteData?.idNumber} • {athleteData?.affiliation}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.total} eventos • {stats.completed} completados
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Información adicional para entrenadores */}
                {actionType?.type === 'coach_request' && (
                  <Box sx={{ 
                    mt: 2,
                    p: 2, 
                    backgroundColor: theme.palette.warning.light + '20',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.warning.main}`
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ¿Qué sucede después?
                    </Typography>
                    <Typography variant="body2" component="div">
                      1. Se enviará una notificación al atleta<br/>
                      2. El atleta puede aceptar o rechazar la invitación<br/>
                      3. Si acepta, aparecerá en tu lista de atletas<br/>
                      4. Recibirás una notificación con su respuesta
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              
              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button 
                  onClick={handleLinkCancel}
                  variant="outlined"
                  size="large"
                  disabled={actionLoading}
                  sx={{ 
                    borderRadius: 2,
                    px: 3
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleLinkConfirm}
                  variant="contained"
                  size="large"
                  startIcon={dialogConfig.confirmIcon}
                  disabled={actionLoading}
                  sx={{ 
                    backgroundColor: actionType?.type === 'coach_request' 
                      ? theme.palette.primary.main 
                      : theme.palette.success.main,
                    '&:hover': {
                      backgroundColor: actionType?.type === 'coach_request' 
                        ? theme.palette.primary.dark 
                        : theme.palette.success.dark,
                    },
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 'bold'
                  }}
                >
                  {actionLoading ? 'Procesando...' : dialogConfig.confirmText}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default AthleteEventProfile;